import React, { useEffect, useMemo, useState } from 'react';
import AgGridService from '../../../../../../../../shared/services/ag-grid/ag-grid.service';
import { AgGridReact } from 'ag-grid-react';
import { CellStyle, ColDef } from 'ag-grid-community';
import useWizardReportDataContext from '../../../../../contexts/useWizardReportData.context';
import IPreviewGridRow from './IPreviewGrid';


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/21/2025
 */
export default function PreviewGridComponent({ }: {}) {
    const { wizardData } = useWizardReportDataContext();
    const { getDefaultColDef, getDefaultProps } = new AgGridService();

    const styleInfo = wizardData.reportSpecification.styleInformation;


    useEffect(() => {
        // Dynamically set CSS variables based on wizardData
        document.documentElement.style.setProperty('--preview-header-text-color', styleInfo.headerForegroundColor || null);
        document.documentElement.style.setProperty('--preview-header-background-color', styleInfo.headerColor || null);
        document.documentElement.style.setProperty('--preview-header-border-color', styleInfo.borderColor || null);
    }, [styleInfo]);


    const getColDefs = useMemo((): ColDef<IPreviewGridRow>[] => {
        let cols: ColDef<IPreviewGridRow>[] = [
            {
                field: "id", headerName: "Emp No",
                editable: false, cellStyle: {} as CellStyle,
                headerClass: "dynamic-preview-header-styles",
            },
            {
                field: "name", headerName: "Name", flex: 1,
                editable: false, cellStyle: {} as CellStyle,
                headerClass: "dynamic-preview-header-styles",
            },
            {
                field: "description", headerName: "Description", flex: 1.5,
                editable: false, cellStyle: {} as CellStyle,
                headerClass: "dynamic-preview-header-styles",
            },

        ];

        cols.forEach((col) => {
            // cell-styles
            col.cellStyle = {
                ...col.cellStyle, // Retain existing cell styles
                // border: `1px solid ${styleInfo.borderColor}`, // Add dynamic border
                borderTop: `1px solid ${styleInfo.borderColor}`,
                borderLeft: `1px solid ${styleInfo.borderColor}`,
            };

        });

        // ---
        return cols;

    }, [styleInfo]);

    const getRowStyle = useMemo(() => {
        return (params: any) => {
            if (params.node.rowIndex % 2 === 0) {
                // Alternate row style
                return {
                    ...(styleInfo.itemColor?.startsWith('#') && { backgroundColor: styleInfo.itemColor }),
                    ...(styleInfo.itemForegroundColor?.startsWith('#') && { color: styleInfo.itemForegroundColor }),
                    // borderBottom: `1px solid ${styleInfo.borderColor}`,
                    // borderLeft: `1px solid ${styleInfo.borderColor}`,
                    // borderRight: `1px solid ${styleInfo.borderColor}`,
                };
            }
            // Default row style
            return {
                ...(styleInfo.alternatingItemColor?.startsWith('#') && { backgroundColor: styleInfo.alternatingItemColor }),
                ...(styleInfo.alternatingItemForegroundColor?.startsWith('#') && { color: styleInfo.alternatingItemForegroundColor }),
                // borderBottom: `1px solid ${styleInfo.borderColor}`,
                // borderLeft: `1px solid ${styleInfo.borderColor}`,
                // borderRight: `1px solid ${styleInfo.borderColor}`,
            };
        };
    }, [styleInfo]);

    const [paginationSizes, setPaginationSizes] = useState<number[]>([20, 50, 100]);
    useEffect(() => {
        if (styleInfo.itemsPerPage) {
            const sizes = new Set([20, +styleInfo.itemsPerPage, 50, 100]);
            setPaginationSizes(Array.from(sizes))
        }
    }, [styleInfo.itemsPerPage]);

    // ---
    return (<div className="ag-theme-quartz"
        style={{ height: "540px", width: "100%", }}>
        <AgGridReact<IPreviewGridRow>
            defaultColDef={{
                ...getDefaultColDef(),
            }}
            columnDefs={getColDefs}
            getRowStyle={getRowStyle}
            {...getDefaultProps({
                rowHeight: 44,
                hideSideBar: true,
                rowDragManaged: true,
            })}
            onFirstDataRendered={(e) => setTimeout(() => e.api.autoSizeColumns(['id']), 100)}

            pagination={styleInfo.usePaginationInWebView?.toLowerCase() === 'true' &&
                Number(styleInfo.itemsPerPage) > 0
            } // Enable pagination
            paginationPageSize={Number(styleInfo.itemsPerPage ?? 20)} // Rows per page
            paginationPageSizeSelector={false}
            // paginationPageSizeSelector={paginationSizes}
            rowData={[
                { id: 1, name: 'Bob Miller', description: 'Keen on learning new tech.' },
                { id: 2, name: 'Hank Smith', description: 'Team player and leader.' },
                { id: 3, name: 'Frank Martinez', description: 'Loves to explore.' },
                { id: 4, name: 'Charlie Brown', description: 'A curious individual.' },
                { id: 5, name: 'Grace Davis', description: 'An enthusiastic learner.' },
                { id: 6, name: 'Ivy Jones', description: 'Avid reader and thinker.' },
                { id: 7, name: 'Eve Martinez', description: 'Team player and leader.' },
                { id: 8, name: 'Charlie Johnson', description: 'Avid reader and thinker.' },
                { id: 9, name: 'Diana Jones', description: 'A lifelong adventurer.' },
                { id: 10, name: 'Bob Johnson', description: 'Creative and innovative.' },
                { id: 11, name: 'Bob Johnson', description: 'Passionate about coding.' },
                { id: 12, name: 'Ivy Smith', description: 'Avid reader and thinker.' },
                { id: 13, name: 'Grace Williams', description: 'Keen on learning new tech.' },
                { id: 14, name: 'Ivy Johnson', description: 'Avid reader and thinker.' },
                { id: 15, name: 'Bob Brown', description: 'An enthusiastic learner.' },
                { id: 16, name: 'Jack Brown', description: 'A lifelong adventurer.' },
                { id: 17, name: 'Eve Miller', description: 'Loves to explore.' },
                { id: 18, name: 'Alice Jones', description: 'A curious individual.' },
                { id: 19, name: 'Alice Rodriguez', description: 'Loves to explore.' },
                { id: 20, name: 'Diana Davis', description: 'Avid reader and thinker.' },
                { id: 21, name: 'Diana Davis', description: 'An enthusiastic learner.' },
                { id: 22, name: 'Grace Garcia', description: 'Avid reader and thinker.' },
                { id: 23, name: 'Diana Martinez', description: 'Team player and leader.' },
                { id: 24, name: 'Bob Brown', description: 'Passionate about coding.' },
                { id: 25, name: 'Grace Davis', description: 'Creative and innovative.' },
            ]}
        />
    </div>)
}